
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserMetric } from '@/types/betaProgram';
import { User, Users, Building2 } from 'lucide-react';

interface UserMetricsSectionProps {
  topUsersByCandidates: UserMetric[];
  topUsersByClients: UserMetric[];
}

export const UserMetricsSection: React.FC<UserMetricsSectionProps> = ({
  topUsersByCandidates,
  topUsersByClients,
}) => {
  const formatEmail = (email: string) => {
    if (email.length > 25) {
      return email.substring(0, 25) + '...';
    }
    return email;
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-600" />
            Top Users by Candidates
          </CardTitle>
          <CardDescription>
            Users who have created the most candidates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topUsersByCandidates.length > 0 ? (
            <div className="space-y-3">
              {topUsersByCandidates.map((user, index) => (
                <div key={user.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm" title={user.user_email}>
                        {formatEmail(user.user_email)}
                      </p>
                      {user.organization_id && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          Org: {user.organization_id.substring(0, 8)}...
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-blue-600">
                      {user.candidate_count} candidates
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No user candidate data available</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-green-600" />
            Top Users by Clients
          </CardTitle>
          <CardDescription>
            Users who have created the most clients
          </CardDescription>
        </CardHeader>
        <CardContent>
          {topUsersByClients.length > 0 ? (
            <div className="space-y-3">
              {topUsersByClients.map((user, index) => (
                <div key={user.user_id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="w-8 h-8 flex items-center justify-center">
                      {index + 1}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm" title={user.user_email}>
                        {formatEmail(user.user_email)}
                      </p>
                      {user.organization_id && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          Org: {user.organization_id.substring(0, 8)}...
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-green-600">
                      {user.client_count} clients
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No user client data available</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
